import type { LinkProps } from '@tanstack/react-router'
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarClock,
  Clapperboard,
  Cpu,
  Database,
  FileClock,
  Images,
  LayoutDashboard,
  LayoutTemplate,
  type LucideIcon,
  MapPin,
  Megaphone,
  Monitor,
  QrCode,
  Radio,
  Settings,
  ShieldCheck,
  Siren,
  Users,
  Boxes,
  Receipt,
  Rocket,
  HeartPulse,
  TriangleAlert,
  ScrollText,
  Variable,
  ListFilter,
  LayoutGrid,
  ListChecks,
  FormInput,
  Workflow,
} from 'lucide-react'
import { hasPermission, type PermissionRequirement } from '@/lib/permissions'
import type { Me } from '@/types/api'

export type AppPath = NonNullable<LinkProps['to']>

export interface NavItem {
  label: string
  to: AppPath
  icon: LucideIcon
  permission?: PermissionRequirement
  /** Not built in Phase 1 — routes to the "coming in a later phase" page. */
  upcoming?: boolean
  /** Not shown in the sidebar; still searchable in the command palette. */
  hidden?: boolean
  /** Plan feature the page needs: hidden when the plan's entitlements are known and exclude it. */
  feature?: string
  keywords?: string[]
}

export interface NavSection {
  label: string | null
  items: NavItem[]
}

export const APP_NAV: NavSection[] = [
  { label: null, items: [{ label: 'Dashboard', to: '/', icon: LayoutDashboard, keywords: ['home', 'overview'] }] },
  {
    label: 'Content',
    items: [
      { label: 'Slideshows', to: '/slideshows', icon: Clapperboard, permission: 'slideshows.view', keywords: ['presentations', 'editor', 'publish', 'content', 'playlist'] },
      { label: 'Channels', to: '/channels', icon: Radio, permission: 'slideshows.view', keywords: ['playlists', 'default content', 'fallback', 'layers'] },
      { label: 'Media', to: '/media', icon: Images, permission: 'media.view', keywords: ['images', 'videos', 'library', 'upload', 'files'] },
      { label: 'Templates', to: '/templates', icon: LayoutTemplate, permission: 'slideshows.view', upcoming: true },
      { label: 'Data Sources', to: '/data-sources', icon: Database, permission: 'slideshows.view', keywords: ['weather', 'rss', 'feeds', 'api', 'json', 'csv', 'bindings', 'live data'] },
      { label: 'Variables', to: '/variables', icon: Variable, permission: 'variables.view', keywords: ['prices', 'daily special', 'overrides', 'locations', 'wifi', 'values', 'placeholders'] },
      { label: 'Local content', to: '/local-content', icon: LayoutGrid, permission: 'content_slots.populate', keywords: ['slots', 'content slots', 'my slots', 'local promotion', 'place content'] },
      { label: 'Slot approvals', to: '/approvals/slots', icon: ListChecks, permission: 'content_slots.approve', keywords: ['approve', 'review', 'pending', 'slots', 'queue'] },
      { label: 'Fields', to: '/fields', icon: FormInput, permission: ['variables.override', 'variables.manage'], keywords: ['template fields', 'edit text', 'daily special', 'local text', 'menu'] },
    ],
  },
  {
    label: 'Campaigns',
    items: [
      { label: 'Campaigns', to: '/campaigns', icon: Megaphone, permission: 'campaigns.manage', keywords: ['advertisers', 'promotions', 'flights'] },
      { label: 'Schedules', to: '/schedules', icon: CalendarClock, permission: 'schedules.manage', keywords: ['rules', 'dayparting', 'priority', 'timing'] },
      { label: 'Conditions', to: '/rules', icon: ListFilter, permission: ['channels.manage', 'campaigns.manage', 'schedules.manage'], keywords: ['rules', 'logic', 'conditional', 'weather', 'only when', 'targeting'] },
      { label: 'Flows', to: '/flows', icon: Workflow, permission: 'flows.view', feature: 'flows', keywords: ['automation', 'signetix flow', 'triggers', 'when then', 'webhooks', 'inbound', 'if this then that'] },
      { label: 'Calendar', to: '/calendar', icon: CalendarDays, permission: ['schedules.manage', 'campaigns.manage'], keywords: ['week', 'month', 'occurrences', 'conflicts'] },
      { label: 'Emergency', to: '/emergency', icon: Siren, permission: 'emergency.broadcast', keywords: ['alert', 'broadcast', 'override', 'evacuation'] },
    ],
  },
  {
    label: 'Displays',
    items: [
      { label: 'Displays', to: '/displays', icon: Monitor, permission: 'displays.view', keywords: ['screens', 'devices', 'fleet', 'health'] },
      { label: 'Players', to: '/players', icon: Cpu, permission: 'displays.view', keywords: ['devices', 'hardware'] },
      { label: 'Groups', to: '/groups', icon: Boxes, permission: 'displays.view', keywords: ['tags', 'labels'] },
      { label: 'Pair player', to: '/pair', icon: QrCode, permission: 'players.manage', hidden: true, keywords: ['pairing', 'code', 'claim', 'connect'] },
      { label: 'Locations', to: '/locations', icon: MapPin, permission: 'displays.view', keywords: ['sites', 'stores', 'venues'] },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { label: 'Overview', to: '/analytics', icon: BarChart3, permission: 'analytics.view', keywords: ['analytics', 'uptime', 'impressions', 'reports'] },
      { label: 'Proof of Play', to: '/analytics/proof-of-play', icon: Activity, permission: 'analytics.view', keywords: ['pop', 'reports', 'export', 'csv', 'plays'] },
    ],
  },
  {
    label: 'Team',
    items: [
      { label: 'Users', to: '/users', icon: Users, keywords: ['members', 'invite', 'team'] },
      { label: 'Roles', to: '/roles', icon: ShieldCheck, permission: ['roles.manage', 'users.manage'], keywords: ['permissions'] },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Notifications', to: '/notifications', icon: Bell, keywords: ['inbox', 'alerts', 'preferences', 'email', 'digest'] },
      { label: 'Audit Log', to: '/audit-log', icon: FileClock, permission: 'audit.view', keywords: ['history', 'activity'] },
      { label: 'Settings', to: '/settings', icon: Settings, keywords: ['organization', 'billing', 'profile', 'security'] },
    ],
  },
]

export const ADMIN_NAV: NavSection[] = [
  {
    label: 'Platform',
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
      { label: 'Tenants', to: '/admin/tenants', icon: Building2 },
      { label: 'Plans', to: '/admin/plans', icon: Receipt },
      { label: 'Audit Logs', to: '/admin/audit-logs', icon: ScrollText },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Player releases', to: '/admin/releases', icon: Rocket, keywords: ['updates', 'rollout', 'versions', 'canary'] },
      { label: 'System health', to: '/admin/health', icon: HeartPulse, keywords: ['queues', 'redis', 'scheduler', 'horizon', 'reverb', 'storage'] },
      { label: 'Failed jobs', to: '/admin/failed-jobs', icon: TriangleAlert, keywords: ['queue', 'retry', 'errors'] },
    ],
  },
]

/**
 * Removes items the user lacks permission for (and, when the plan's feature entitlements are
 * given, items whose plan feature is not included), then empty sections.
 */
export function filterNav(sections: NavSection[], permissions: readonly string[] | undefined, features?: Readonly<Record<string, boolean>> | null): NavSection[] {
  const included = (item: NavItem) => !item.feature || !features || features[item.feature] === true
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => hasPermission(permissions, item.permission) && included(item)) }))
    .filter((section) => section.items.length > 0)
}

/**
 * Where the signed-in user manages their own security (password, 2FA, sessions). The tenant app's
 * `/settings/security` needs a current organization; a platform admin without one would be bounced to
 * `/admin`, which refuses them until 2FA is on — so they get the admin console's copy.
 */
export function securityPath(me: Pick<Me, 'current_tenant' | 'user'>): '/settings/security' | '/admin/security' {
  return !me.current_tenant && me.user.is_platform_admin ? '/admin/security' : '/settings/security'
}
